import React, { useState, useEffect, useContext } from 'react';
import { Grid, Tooltip, Tab, Tabs, styled } from '@material-ui/core';
import {
  SdmTypography,
  SdmCircularLoader,
  SdmScrollable,
  SdmIconButtonLoader,
  SdmRichText,
  SdmSpacer,
} from 'components';
import { breakoutRoomInviteModel } from 'entities/breakoutRoomInviteModel';
import { usePubnabLambdaServices } from 'utils/pubnubLambdaHelper';
import { useDataHelper } from 'utils/dataHelper';
import { Block as BanIcon, LockOpen as UnbanIcon } from '@material-ui/icons';
import { getUUID } from 'utils/useChat';
import { theme } from 'theme';
import { isUnbanEnabled, cdnUrl as CDN_URL } from 'utils/constants';
import { SizeContext } from 'context/SizeContextProvider';
import SpeakerCard from './chatSession/SpeakerCard';

const CustomTabs = styled(Tabs)({
  width: '100%',
  marginBottom: 40,
  '& .MuiButtonBase-root': {
    width: '50%',
    borderBottom: '1px solid white',
    maxWidth: '100%',
  },
  '& .MuiTab-wrapper': {
    textTransform: 'capitalize',
  },
});

const InvitedContainer = ({
  i18n,
  occupants,
  channel,
  session,
  userMetadata,
  handleBan,
  handleUnban,
  isModerator,
  // hasBeenBanned,
  chatInfo,
  fetchUsersMetadata,
  isSmDown,
}) => {
  const tabs = {
    INFO: 'info',
    PARTICIPANTS: 'participants',
  };
  const dataHelper = useDataHelper();

  //States
  const [invitedUsers, setInvitedUsers] = useState();
  const [users, setUsers] = useState([]);
  const [vips, setVips] = useState([]);
  const [selectedTab, setSelectedTab] = useState(tabs.INFO);

  //Context
  const { getInnerHeight } = useContext(SizeContext);

  const CustomContainer = styled('div')({
    '& ::-webkit-scrollbar': {
      width: 6,
    },
    '& ::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '& ::-webkit-scrollbar-thumb': {
      background: 'rgba(149, 152, 154, 0.43)',
      borderRadius: 5,
    },
    '& ::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(149, 152, 154, 1)',
    },
  });

  useEffect(() => {
    if (channel) {
      //chiamare la lambda per tutti gli invitati (solo se moderatore) visualizzare gli occupants se utente
      fetchInvitations();
    }
  }, [channel]);

  useEffect(() => {
    if (invitedUsers && occupants) {
      fetchOccupants();
    }
  }, [invitedUsers, occupants]);

  const fetchInvitations = async () => {
    const { getInvitations, getInvitationsForUser } = usePubnabLambdaServices({
      sessionId: session,
      accessToken: userMetadata.id,
    });
    const res = await (isModerator
      ? getInvitations()
      : getInvitationsForUser());

    //console.log('INVITATIONS RESPONSE:: ', res);
    if (res) {
      const _users = res.map((invite) => ({
        ...invite.user,
        invitationType: invite.invitationType,
        jobTitle: invite?.jobTitle,
      }));
      const cleared = _users.filter((utn) => utn.id !== userMetadata.id);
      if (isModerator) {
        for (let i = 0; i < cleared.length; i++) {
          const utn = cleared[i];
          const utnMetadata = await fetchUsersMetadata(getUUID(utn.id));
          // console.log(
          //   '🚀 ~ file: InvitedContainer.js ~ line 73 ~ fetchInvitations ~ utnMetadata',
          //   utnMetadata
          // );
          cleared[i] = {
            ...utn,
            isBanned: utnMetadata?.isBanned || false,
          };
        }
      }
      setInvitedUsers(cleared);
    } else {
      setInvitedUsers([]);
    }
  };

  const fetchOccupants = async () => {
    const res = await breakoutRoomInviteModel.gql.listBreakoutRoomInvitations(
      session
    );
    const _brand = res?.brand;
    res?.invitations?.items?.push({
      invitationType: 'vip',
      user: { id: _brand.id, givenName: _brand.name || 'Host' },
    });
    let cleared = '';
    if (res) {
      //Add filter to return only accepted or the moderator
      const _users = res?.invitations?.items
        ?.filter(
          (i) =>
            i.status === breakoutRoomInviteModel.enums.status.ACCEPTED ||
            i?.user?.id === _brand?.id
        )
        .map((invite) => ({
          ...invite.user,
          invitationType: invite.invitationType,
          jobTitle: invite?.jobTitle,
        }));

      cleared = _users.filter((utn) => utn.id !== userMetadata.id);
      if (isModerator) {
        for (let i = 0; i < cleared.length; i++) {
          const utn = cleared[i];
          const utnMetadata = await fetchUsersMetadata(getUUID(utn.id));
          // console.log(
          //   '🚀 ~ file: InvitedContainer.js ~ line 73 ~ fetchInvitations ~ utnMetadata',
          //   utnMetadata
          // );
          cleared[i] = {
            ...utn,
            isBanned: utnMetadata?.isBanned || false,
          };
        }
      }
    }

    const _users = occupants
      .map((o) => {
        if (o.state) {
          return {
            ...o.state.user,
            uuid: o.uuid,
            isOnline: o.state.isOnline,
          };
        }
        return null;
      })
      .filter((u) => !!u);

    const vipOnline = [];
    const vipOffline = [];
    const userOnline = [];
    const userOffline = [];

    cleared.map((utn) => {
      const presence = _users.find((u) => {
        return u.id === utn.id;
      });
      if (
        utn.invitationType === breakoutRoomInviteModel.enums.invitationTypes.VIP
      ) {
        if (presence?.isOnline) vipOnline.push(utn);
        else vipOffline.push(utn);
      } else {
        if (presence?.isOnline) userOnline.push(utn);
        else userOffline.push(utn);
      }
    });
    setVips([...vipOnline, ...vipOffline]);
    setUsers([...userOnline, ...userOffline]);
  };

  const handleAction = async (isBanAction, user) => {
    const res = await (isBanAction ? handleBan : handleUnban)(getUUID(user.id));
    if (!res?.message?.error) {
      const index = invitedUsers.findIndex((utn) => utn.id === user.id);
      invitedUsers[index] = {
        ...user,
        isBanned: isBanAction,
      };
      setInvitedUsers(() => [...invitedUsers]);
    }
    return res;
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const renderActionButton = (icon, action, tootlipText) => {
    return (
      <Grid item>
        <Tooltip title={tootlipText}>
          <div>
            <SdmIconButtonLoader callback={action}>{icon}</SdmIconButtonLoader>
          </div>
        </Tooltip>
      </Grid>
    );
  };

  const renderUserInfo = (user, idx) => {
    let info = {
      id: user.id,
      isBanned: user.isBanned,
    };
    let isOnline = false;

    const uuid = user.id;
    //TODO rinominare
    const presenceUser = occupants.find((o) => {
      return o.state.user.id === uuid;
    });
    if (presenceUser) {
      isOnline = presenceUser.state.isOnline;
    }
    info = {
      ...info,
      nickname: `${user.givenName} ${user.familyName ? user.familyName : ''}`,
      initials: `${user.givenName.charAt(0)}${
        user.familyName ? user.familyName.charAt(0) : ''
      }`,
      isOnline: isOnline,
      jobTitle: user?.jobTitle,
    };

    return (
      <Grid
        container
        key={idx}
        style={{
          padding: 4,
          borderRadius: '32px 0 0 32px',
          marginBottom: 6,
          alignItems: 'center',
        }}
      >
        <Grid item xs={2}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '100%',
              backgroundColor: theme.colors.primary.red,
              position: 'relative',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
            }}
          >
            <SdmTypography
              variant={'h2Regular'}
              style={{ color: 'rgb(255,255,255)', lineHeight: 2 }}
              textAlign={'center'}
            >
              {info.initials || ''}
            </SdmTypography>
            {info.isOnline && (
              <div
                style={{
                  width: 14,
                  height: 14,
                  border: '1px solid white',
                  borderRadius: '100%',
                  backgroundColor: '#2AE128',
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                }}
              />
            )}
          </div>
        </Grid>
        <Grid item xs={isModerator ? 8 : 10}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              flexDirection: 'column',
              color: theme.colors.primary.white,
            }}
          >
            <SdmTypography
              variant={'h4Regular'}
              margin={0}
              style={{
                marginLeft: 8,
                ...(info.isBanned && {
                  color: 'red',
                }),
              }}
            >
              {info.nickname}
            </SdmTypography>
            {info.jobTitle && (
              <SdmTypography
                variant={'text'}
                margin={0}
                style={{
                  marginLeft: 8,
                  ...(info.isBanned && {
                    color: 'red',
                  }),
                }}
              >
                {info.jobTitle}
              </SdmTypography>
            )}
          </div>
        </Grid>
        {isModerator ? (
          <Grid item xs={2}>
            <Grid
              container
              style={{
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'flex-end',
                color: theme.colors.primary.white,
              }}
            >
              {!info.isBanned
                ? handleBan
                  ? renderActionButton(
                      <BanIcon />,
                      async () => {
                        await handleAction(true, user);
                      },
                      i18n.ban
                    )
                  : null
                : isUnbanEnabled && handleUnban
                ? renderActionButton(
                    <UnbanIcon />,
                    async () => {
                      await handleAction(false, user);
                    },
                    i18n.unban
                  )
                : null}
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    );
  };

  return (
    <CustomContainer
      style={{
        padding: 16,
        height: `calc(${getInnerHeight()}px - 122px)`,
        zIndex: 30,
      }}
    >
      <Grid container style={{ height: isSmDown ? '90%' : '100%' }}>
        <Grid item xs={12}>
          <CustomTabs value={selectedTab} onChange={handleChangeTab}>
            {Object.values(tabs).map((value) => {
              return (
                <Tab
                  label={
                    <SdmTypography
                      variant={'h2Regular'}
                      style={{
                        color: 'white',
                        fontSize: 24,
                      }}
                      textAlign={'center'}
                    >
                      {i18n.tabs[value]}
                    </SdmTypography>
                  }
                  value={value}
                  key={value}
                />
              );
            })}
          </CustomTabs>
          <SdmSpacer style={{ height: 8 }} />
        </Grid>
        <Grid item xs={12} style={{ height: '100%' }}>
          {selectedTab === tabs.INFO ? (
            <>
              {chatInfo && (
                <>
                  <img
                    src={
                      chatInfo.coverImage
                        ? `${CDN_URL}/${chatInfo.coverImage}`
                        : '/themes/custom/sdm/images/mail/breakout-room-img.jpg'
                    }
                    style={{
                      width: '100%',
                      maxHeigth: 265,
                      objectFit: 'contain',
                    }}
                  />
                  <SdmTypography
                    variant={'h1Regular'}
                    style={{ color: 'white', marginTop: 20 }}
                    margin={0}
                  >
                    {chatInfo.name}
                  </SdmTypography>
                  {chatInfo.description ? (
                    <SdmTypography
                      variant={'h4Regular'}
                      margin={0}
                      style={{
                        color: theme.colors.primary.white,
                        margin: '8px 0',
                      }}
                      textAlign={isSmDown ? 'center' : 'left'}
                    >
                      <SdmRichText
                        value={dataHelper.parseRichText(chatInfo.description)}
                        variant={'transparent'}
                        formControlStyle={{
                          margin: 0,
                        }}
                        editorStyleOverride={{
                          minHeight: 96,
                          maxHeight: 300,
                          padding: 0,
                          margin: 0,
                        }}
                        readOnly
                      />
                    </SdmTypography>
                  ) : null}
                  <SdmSpacer height={50} />
                  {chatInfo?.speakers?.items.length ? (
                    <>
                      <SdmTypography
                        variant={'h2Regular'}
                        style={{ color: 'white', marginBottom: 20 }}
                        margin={0}
                      >
                        {i18n.speakers}
                      </SdmTypography>
                      <Grid container>
                        {chatInfo?.speakers?.items.map((sp) => (
                          <Grid item sm={12} md={6} key={sp.id}>
                            <SpeakerCard speaker={sp} isSmDown={isSmDown} />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  ) : null}
                  <SdmSpacer height={50} />
                </>
              )}
            </>
          ) : (
            <SdmScrollable
              style={{
                overflowX: 'hidden',
              }}
            >
              <SdmTypography
                variant={'h2Regular'}
                style={{ color: 'rgba(255,255,255, 0.7)' }}
                textAlign={'center'}
              >
                {i18n.vipOnline} {!occupants ? <SdmCircularLoader /> : null}
              </SdmTypography>
              {vips && vips.length > 0 ? (
                vips.map((u, idx) => {
                  return renderUserInfo(u, idx, true);
                })
              ) : (
                <SdmTypography
                  variant={'h3'}
                  style={{ color: 'rgba(255,255,255, 0.7)' }}
                  textAlign={'center'}
                >
                  {i18n.noVipOnline}
                </SdmTypography>
              )}
              <SdmSpacer height={40} />
              <SdmTypography
                variant={'h2Regular'}
                style={{ color: 'rgba(255,255,255, 0.7)' }}
                textAlign={'center'}
              >
                {i18n.invitedUsers} {!occupants ? <SdmCircularLoader /> : null}
              </SdmTypography>
              {users && users.length > 0 ? (
                users.map((u, idx) => {
                  return renderUserInfo(u, idx);
                })
              ) : (
                <SdmTypography
                  variant={'h3'}
                  style={{ color: 'white' }}
                  textAlign={'center'}
                >
                  {i18n.noUserOnline}
                </SdmTypography>
              )}
            </SdmScrollable>
          )}
        </Grid>
      </Grid>
    </CustomContainer>
  );
};

export default InvitedContainer;
